import { AgGridModule } from '@ag-grid-community/angular';
import { GridOptions } from '@ag-grid-community/core';
import { ColDef } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { ISetFilterParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iSetFilter';
import { ModuleRegistry } from '@ag-grid-community/core/dist/esm/es6/modules/moduleRegistry';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model/dist/esm/es6/serverSideRowModelModule';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter/dist/esm/es6/setFilterModule';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar/dist/esm/es6/statusBarModule';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DateFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-floating-filter/date-floating-filter.component';
import { NotificationStatusFloatingFilterComponent } from 'shared/components/ag-grid/filters/notification-status-floating-filter/notification-status-floating-filter.component';
import { BEEnumToNotificationStatus, NotificationStatusEnum } from 'shared/constants/notification-statuses';
import { EventBusService, ToastService } from 'shared/services';
import { FileService } from 'shared/services/file.service';
import { NotificationsGridService } from 'shared/services/notifications-grid.service';
import { pascalToCamelCaseRecursive } from 'shared/util/pascal-to-camel-case';
import { BEEnumToUserNotificationType } from 'users/enums/user-notification-type.enum';
import { UserNotification } from 'users/interfaces/user-notification.interface';
import { UserNotificationActionsService } from 'users/services/user-notification-actions.service';

@Component({
    selector: 'app-user-notifications-table',
    templateUrl: './user-notifications-table.component.html',
    styleUrls: ['./user-notifications-table.component.scss'],
    imports: [CommonModule, AgGridModule, TranslateModule],
})
export class UserNotificationsTableComponent extends BaseGrid implements OnInit, OnDestroy {
    public format: string = 'YYYY-MM-DD';

    constructor(
        router: Router,
        activatedRoute: ActivatedRoute,
        destroyRef: DestroyRef,
        private notificationsGridService: NotificationsGridService,
        private fileService: FileService,
        private eventBusService: EventBusService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private changeDetectorRef: ChangeDetectorRef,
        private userNotificationActionsService: UserNotificationActionsService,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(notificationsGridService, router, activatedRoute, destroyRef);
    }

    public extendedGridOptions: GridOptions = {
        ...this.gridOptions,
        onGridReady: (event) => {
            super.onGridReady(event);

            // auto-size the status column to fit contents
            this.agGrid.api.autoSizeColumn(this.agGrid.api.getColumn('status')!);
        },
    };

    public columnDefs: ColDef[] = [
        {
            field: 'operationName',
            minWidth: 200,
            flex: 2,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            wrapText: true,
            autoHeight: true,
            suppressSizeToFit: true,
            cellClass: 'wrap-content',
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'startDateTime',
            valueFormatter: this.dateFormatter,
            floatingFilter: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            filterParams: { format: this.format },
            resizable: false,
            minWidth: 200,
            maxWidth: 200,
            floatingFilterComponent: DateFloatingFilterComponent,
            unSortIcon: true,
        },
        {
            field: 'completedDateTime',
            valueFormatter: this.dateFormatter,
            floatingFilter: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            filterParams: { format: this.format },
            resizable: false,
            minWidth: 200,
            maxWidth: 200,
            floatingFilterComponent: DateFloatingFilterComponent,
            unSortIcon: true,
        },
        {
            field: 'status',
            filter: 'agSetColumnFilter',
            filterParams: {
                excelMode: 'windows',
                suppressSorting: true,
                suppressMiniFilter: true,
                cellRenderer: 'notificationStatusCellRenderer',
                cellRendererParams: { isFloatingFilterIcon: true },
                values: (params: any) => {
                    const values = Object.values(NotificationStatusEnum);
                    // supply values to the set filter
                    params.success(values);
                },
            } as unknown as ISetFilterParams,
            floatingFilter: true,
            floatingFilterComponent: NotificationStatusFloatingFilterComponent,
            floatingFilterComponentParams: { suppressFilterButton: true },
            unSortIcon: false,
            resizable: false,
            suppressSizeToFit: true,
            cellRenderer: 'notificationStatusCellRenderer',
        },
        {
            field: 'notes',
            minWidth: 200,
            flex: 3,
            floatingFilter: false,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            wrapText: true,
            autoHeight: true,
            suppressSizeToFit: true,
            cellClass: 'wrap-content',
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            headerName: '',
            field: 'actions',
            headerComponent: 'resetFiltersHeader',
            filter: false,
            resizable: false,
            sortable: false,
            minWidth: 100,
            maxWidth: 150,
            flex: 1,
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: (params: any) => {
                const notification = params.data as UserNotification;

                return {
                    buttons: [
                        // Legacy report download button
                        {
                            clickAction: this.downloadButtonClickHandler.bind(this),
                            icon: 'download',
                            tooltip: 'generalTooltips.download',
                            computeVisibleState: this.isDownloadButtonVisible.bind(this),
                        },

                        // Dynamic list of buttons based on notification type
                        ...this.userNotificationActionsService.getActionButtonsForNotification(notification).map((config) => ({
                            clickAction: () => config.clickAction(),
                            icon: config.icon,
                            tooltip: config.tooltip,
                            computeVisibleState: () => config.visible,
                        })),
                    ],
                };
            },
            suppressSizeToFit: true,
            enableValue: false,
            enablePivot: false,
            enableRowGroup: false,
        },
    ];

    public ngOnInit(): void {
        this.eventBusService
            .getObservableForEventStream('notification')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((notification: any) => {
                if (!notification) {
                    return;
                }

                const mappedNotification = pascalToCamelCaseRecursive(notification) as UserNotification;
                mappedNotification.status = BEEnumToNotificationStatus[Number(mappedNotification.status)];
                mappedNotification.notificationType = BEEnumToUserNotificationType[Number(mappedNotification.notificationType)];

                const renderedRows = this.agGrid.api.getRenderedNodes().map((node) => node.data);
                const row = renderedRows?.find((node: any) => node.id == mappedNotification.id);

                if (!row) {
                    const isFilterEmpty = Object.keys(this.agGrid.api.getFilterModel()).length === 0;
                    if (!isFilterEmpty) {
                        this.agGrid.api.refreshServerSide({ purge: true });
                        return;
                    }

                    if (this.pageNumber() != 1) {
                        if (mappedNotification.status != NotificationStatusEnum.IN_PROGRESS) {
                            this.toastService.showToast(
                                this.translateService.instant('userNotificationsTable.newNotificationsMessage'),
                                undefined,
                                undefined,
                                10000,
                                'right',
                                () => {
                                    this.onPageChangedHandler(this.pageSize(), 1);
                                },
                            );
                        }
                        return;
                    }

                    this.agGrid.api.applyServerSideTransaction({
                        route: [],
                        addIndex: 0,
                        add: [mappedNotification],
                    });
                    this.updateRowCount((this.totalRowCount() ?? 0) + 1);
                } else {
                    const transactionResult = this.agGrid.api.applyServerSideTransaction({
                        route: [],
                        update: [mappedNotification],
                    });

                    if (transactionResult && transactionResult.update && transactionResult.update.length > 0) {
                        const updatedNode = transactionResult.update[0];
                        this.agGrid.api.refreshCells({ rowNodes: [updatedNode], columns: ['actions'], force: true });
                    }
                }
            });
    }

    public downloadButtonClickHandler(rowData: any) {
        this.fileService.downloadExcelFile(rowData.downloadLink);
    }

    private isDownloadButtonVisible(rowData: any): boolean {
        return rowData.downloadLink !== null && rowData.downloadLink !== undefined && rowData.downloadLink.trim() !== '';
    }
}
